/**
 * Centralized React Flow Canvas
 *
 * Single, reusable React Flow implementation for all workflow editors.
 * Domain-agnostic: all data comes from props (database/parameters).
 * DRY: eliminates duplication across DAGVisualization, WorkflowDesigner, etc.
 * Life-critical: comprehensive error handling, validation, audit trails.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  Node,
  OnSelectionChangeParams,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useNodeTypes } from './NodeRegistry';
import {
  BaseEdgeData,
  BaseNodeData,
  CanvasConfig,
  ExecutionContext,
  ExecutionStatus,
  FlowEdge,
  FlowNode,
  ReplayState,
} from './types';

export interface ReactFlowCanvasProps {
  // Core data (from database/API)
  nodes: FlowNode<BaseNodeData>[];
  edges: FlowEdge<BaseEdgeData>[];

  // Configuration (data-driven)
  config?: CanvasConfig;

  // Execution context (for live monitoring)
  executionContext?: ExecutionContext;

  // Replay state (for execution playback)
  replayState?: ReplayState;

  // Selection state (controlled)
  selectedNodeIds?: string[];
  selectedEdgeIds?: string[];

  // Change handlers
  onNodesChange?: (nodes: FlowNode<BaseNodeData>[]) => void;
  onEdgesChange?: (edges: FlowEdge<BaseEdgeData>[]) => void;
  onNodePositionChange?: (nodeId: string, position: { x: number; y: number }) => void;

  // Custom node types (optional override/extension)
  nodeTypes?: Record<string, React.ComponentType<any>>;

  // Custom styling
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Inner component (inside ReactFlowProvider)
 */
const ReactFlowCanvasInner: React.FC<ReactFlowCanvasProps> = ({
  nodes: inputNodes,
  edges: inputEdges,
  config = {},
  executionContext,
  replayState,
  selectedNodeIds = [],
  selectedEdgeIds = [],
  onNodesChange: onNodesChangeProp,
  onEdgesChange: onEdgesChangeProp,
  onNodePositionChange,
  nodeTypes: customNodeTypes,
  className,
  style,
}) => {
  const reactFlow = useReactFlow();
  const nodeTypes = useNodeTypes(customNodeTypes);
  const lastUpdateRef = useRef<number>(0);

  console.log('[ReactFlowCanvas] Received nodes:', inputNodes.length, inputNodes);
  console.log('[ReactFlowCanvas] Received edges:', inputEdges.length, inputEdges);
  console.log('[ReactFlowCanvas] Custom node types:', Object.keys(customNodeTypes || {}));

  // Convert input data to React Flow format with proper typing
  const initialNodes = useMemo(() => {
    return inputNodes.map((node) => ({
      ...node,
      // Ensure position exists
      position: node.position || { x: 0, y: 0 },
      // Ensure type matches node data type
      type: node.type || node.data.type,
      // Selection state
      selected: selectedNodeIds.includes(node.id),
      // Draggable from config
      draggable: config.nodesDraggable !== false,
      // Focusable from config
      focusable: config.nodesFocusable !== false,
    }));
  }, [inputNodes, selectedNodeIds, config.nodesDraggable, config.nodesFocusable]);

  const initialEdges = useMemo(() => {
    return inputEdges.map((edge) => ({
      ...edge,
      // Selection state
      selected: selectedEdgeIds.includes(edge.id),
      // Focusable from config
      focusable: config.edgesFocusable !== false,
      // Default styling from data
      style: {
        stroke: edge.data?.renderConfig?.color || '#94a3b8',
        strokeWidth: edge.data?.renderConfig?.strokeWidth || 2,
        ...edge.style,
      },
      // Animated from config or data
      animated: edge.data?.renderConfig?.animated || edge.animated || false,
      // Marker from data or default
      markerEnd: edge.markerEnd || {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
      },
    }));
  }, [inputEdges, selectedEdgeIds, config.edgesFocusable]);

  // React Flow state management
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState(initialEdges);

  console.log('[ReactFlowCanvas] React Flow nodes state:', nodes.length, nodes);
  console.log('[ReactFlowCanvas] React Flow edges state:', edges.length, edges);

  // Sync external nodes to internal state (with proper dependency tracking)
  useEffect(() => {
    // Only update if node count or IDs changed
    const nodeIds = initialNodes
      .map((n) => n.id)
      .sort()
      .join(',');
    const currentIds = nodes
      .map((n) => n.id)
      .sort()
      .join(',');

    if (nodeIds !== currentIds) {
      console.log(
        '[ReactFlowCanvas] Syncing nodes - count changed from',
        nodes.length,
        'to',
        initialNodes.length,
      );
      setNodes(initialNodes);
    }
  }, [initialNodes]); // Don't include setNodes to avoid loop

  // Sync external edges to internal state (with proper dependency tracking)
  useEffect(() => {
    // Only update if edge count or IDs changed
    const edgeIds = initialEdges
      .map((e) => e.id)
      .sort()
      .join(',');
    const currentIds = edges
      .map((e) => e.id)
      .sort()
      .join(',');

    if (edgeIds !== currentIds) {
      console.log(
        '[ReactFlowCanvas] Syncing edges - IDs changed',
        '\n  Current IDs:',
        currentIds,
        '\n  New IDs:',
        edgeIds,
        '\n  Current count:',
        edges.length,
        '\n  New count:',
        initialEdges.length,
      );
      setEdges(initialEdges);
    }
  }, [initialEdges]); // Don't include setEdges and edges to avoid loop

  // Update nodes based on execution context (live monitoring)
  useEffect(() => {
    if (!executionContext) return;

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        let status: ExecutionStatus | undefined;

        if (executionContext.completedNodes?.has(node.id)) {
          status = ExecutionStatus.Completed;
        } else if (executionContext.failedNodes?.has(node.id)) {
          status = ExecutionStatus.Failed;
        } else if (executionContext.runningNodes?.has(node.id)) {
          status = ExecutionStatus.Running;
        } else if (executionContext.pausedNodes?.has(node.id)) {
          status = ExecutionStatus.Paused;
        }

        if (status && status !== node.data.status) {
          return {
            ...node,
            data: {
              ...node.data,
              status,
            },
          };
        }

        return node;
      }),
    );
  }, [executionContext, setNodes]);

  // Update nodes based on replay state
  useEffect(() => {
    if (!replayState || !replayState.isReplaying) return;

    const currentStep = replayState.currentStep;
    const history = replayState.executionHistory.slice(0, currentStep + 1);

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const latestStep = history.filter((step) => step.nodeId === node.id).pop();

        if (latestStep && latestStep.status !== node.data.status) {
          return {
            ...node,
            data: {
              ...node.data,
              status: latestStep.status,
              executionResult: {
                ...node.data.executionResult,
                duration: latestStep.duration,
                data: latestStep.data,
                outputs: latestStep.outputs as Record<string, unknown>,
              },
            },
          };
        }

        return node;
      }),
    );
  }, [replayState, setNodes]);

  // Handle node changes
  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChangeInternal>[0]) => {
      onNodesChangeInternal(changes);

      // Notify parent of changes
      if (onNodesChangeProp) {
        setNodes((currentNodes) => {
          onNodesChangeProp(currentNodes);
          return currentNodes;
        });
      }
    },
    [onNodesChangeInternal, onNodesChangeProp, setNodes],
  );

  // Handle edge changes
  const handleEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChangeInternal>[0]) => {
      console.log('[ReactFlowCanvas] Edge changes:', changes);
      onEdgesChangeInternal(changes);

      // Notify parent of changes
      if (onEdgesChangeProp) {
        setEdges((currentEdges) => {
          console.log('[ReactFlowCanvas] Notifying parent of edge changes:', currentEdges.length);
          onEdgesChangeProp(currentEdges);
          return currentEdges;
        });
      }
    },
    [onEdgesChangeInternal, onEdgesChangeProp, setEdges],
  );

  // Handle connection
  const handleConnect = useCallback(
    async (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      // Validation if enabled
      if (config.validateOnConnect) {
        // Custom validation hook from config
        const isValid = await config.onConnect?.(connection.source, connection.target);
        if (isValid === false) return;
      } else if (config.onConnect) {
        await config.onConnect(connection.source, connection.target);
      }
    },
    [config],
  );

  // Handle node drag stop
  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      onNodePositionChange?.(node.id, node.position);
    },
    [onNodePositionChange],
  );

  // Handle selection change
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes, edges: selectedEdges }: OnSelectionChangeParams) => {
      const nodeIds = selectedNodes.map((n) => n.id);
      const edgeIds = selectedEdges.map((e) => e.id);

      config.onSelectionChange?.(nodeIds, edgeIds);
    },
    [config],
  );

  // Handle node click
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node<BaseNodeData>) => {
      config.onNodeClick?.(node.id, node.data);
    },
    [config],
  );

  // Handle edge click
  const handleEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: Edge<BaseEdgeData>) => {
      config.onEdgeClick?.(edge.id, edge.data!);
    },
    [config],
  );

  // Handle node double click
  const handleNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: Node<BaseNodeData>) => {
      config.onNodeDoubleClick?.(node.id, node.data);
    },
    [config],
  );

  // Handle edge double click
  const handleEdgeDoubleClick = useCallback(
    (_event: React.MouseEvent, edge: Edge<BaseEdgeData>) => {
      config.onEdgeDoubleClick?.(edge.id, edge.data!);
    },
    [config],
  );

  // Handle nodes delete
  const handleNodesDelete = useCallback(
    (deletedNodes: Node<BaseNodeData>[]) => {
      config.onNodesDelete?.(deletedNodes.map((n) => n.id));
    },
    [config],
  );

  // Handle edges delete
  const handleEdgesDelete = useCallback(
    (deletedEdges: Edge<BaseEdgeData>[]) => {
      config.onEdgesDelete?.(deletedEdges.map((e) => e.id));
    },
    [config],
  );

  // Fit view when nodes change (especially on load)
  useEffect(() => {
    if (config.fitView && nodes.length > 0) {
      console.log('[ReactFlowCanvas] Fitting view to', nodes.length, 'nodes');
      setTimeout(() => {
        reactFlow.fitView({ padding: 0.2, duration: 800, maxZoom: 1 });
        console.log('[ReactFlowCanvas] Fit view executed');
      }, 150); // Slightly longer delay to ensure DOM is ready
    }
  }, [nodes.length, config.fitView, reactFlow]); // Trigger on node count change

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        ...style,
      }}
      className={className}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onNodeDragStop={handleNodeDragStop}
        onSelectionChange={handleSelectionChange}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeDoubleClick={handleEdgeDoubleClick}
        onNodesDelete={handleNodesDelete}
        onEdgesDelete={handleEdgesDelete}
        nodeTypes={nodeTypes}
        fitView={config.fitView}
        fitViewOptions={{ padding: 0.2, maxZoom: 1.2, duration: 800 }}
        minZoom={0.1}
        maxZoom={4}
        nodesDraggable={config.nodesDraggable !== false}
        nodesConnectable={config.nodesConnectable !== false}
        nodesFocusable={config.nodesFocusable !== false}
        edgesFocusable={config.edgesFocusable !== false}
        onlyRenderVisibleElements={config.onlyRenderVisibleElements}
      >
        {config.showBackground !== false && (
          <Background gap={16} color="var(--mantine-color-gray-4)" />
        )}
        {config.showControls !== false && <Controls position="bottom-right" />}
        {config.showMiniMap !== false && (
          <MiniMap
            zoomable
            pannable
            nodeColor={(node: any) => {
              const status = node.data?.status;
              if (!status) return '#94a3b8';

              switch (status) {
                case ExecutionStatus.Completed:
                  return '#10b981';
                case ExecutionStatus.Failed:
                  return '#ef4444';
                case ExecutionStatus.Running:
                  return '#f59e0b';
                case ExecutionStatus.RequiresHuman:
                  return '#8b5cf6';
                case ExecutionStatus.Paused:
                  return '#06b6d4';
                default:
                  return '#94a3b8';
              }
            }}
          />
        )}
      </ReactFlow>
    </div>
  );
};

/**
 * Main component (wraps with ReactFlowProvider)
 */
export const ReactFlowCanvas: React.FC<ReactFlowCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ReactFlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
};

/**
 * Export Handle for custom node implementations
 */
export { Handle, Position as HandlePosition } from 'reactflow';
export type { NodeProps } from 'reactflow';
